import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getToken } from "../../services/Token";
import {
  useGetMyResignationStatusQuery,
  useSubmitResignationMutation,
  useWithdrawResignationMutation,
} from "../../api/resignationApi";
import {
  RESIGNATION_STATUS,
  apiErrorMessage,
  effectiveStatus,
  formatDate,
  formatNoticePeriod,
  normalizeResignation,
} from "./constants";
import StatusBadge from "./StatusBadge";
import FlowProgress from "./FlowProgress";
import ResignationActivity from "./ResignationActivity";
import DocumentList from "./DocumentList";
import UploadDocuments from "./UploadDocuments";
import ReasonDialog from "./ReasonDialog";

const DEFAULT_NOTICE_PERIOD_DAYS = 60;

const Field = ({ label, value }) => (
  <div>
    <p className="text-xs uppercase tracking-wide text-gray-400">{label}</p>
    <p className="mt-1 text-sm text-gray-700">{value || "-"}</p>
  </div>
);

const EmployeeResignation = () => {
  const { data, isLoading, isError, error, refetch } = useGetMyResignationStatusQuery();
  const [submitResignation, { isLoading: isSubmitting }] = useSubmitResignationMutation();
  const [withdrawResignation, { isLoading: isWithdrawing }] = useWithdrawResignationMutation();

  const [reason, setReason] = useState("");
  const [noticePeriodDays, setNoticePeriodDays] = useState(String(DEFAULT_NOTICE_PERIOD_DAYS));
  const [showWithdraw, setShowWithdraw] = useState(false);

  const record = normalizeResignation(data);
  const status = effectiveStatus(record);
  // The record carries the id the backend expects on every write; the token copy is
  // only a fallback for the very first call, before any record exists.
  const employeeId = record?.employeeId || getToken("employeeId");

  // If the backend already knows the employee's notice period, show that instead of
  // the generic default.
  useEffect(() => {
    if (record?.noticePeriodDays > 0) {
      setNoticePeriodDays(String(record.noticePeriodDays));
    }
  }, [record?.noticePeriodDays]);

  // A 404 simply means this employee has never resigned - that is the IDLE state,
  // not an error worth showing.
  const isRealError = isError && error?.status !== 404;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!reason.trim()) {
      toast.error("Please enter a reason for your resignation.");
      return;
    }
    const days = Number(noticePeriodDays);
    if (!Number.isFinite(days) || days < 0) {
      toast.error("Please enter a valid notice period.");
      return;
    }
    try {
      await submitResignation({
        employeeId,
        reason: reason.trim(),
        NoticePeriodDays: days,
      }).unwrap();
      setReason("");
      toast.success("Resignation submitted for approval.");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to submit your resignation."));
    }
  };

  const handleWithdraw = async () => {
    try {
      await withdrawResignation({ employeeId }).unwrap();
      setShowWithdraw(false);
      toast.success("Resignation withdrawn.");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to withdraw your resignation."));
    }
  };

  if (isLoading) {
    return <p className="p-6 text-sm text-gray-500">Loading your resignation status...</p>;
  }

  if (isRealError) {
    return (
      <div className="p-6">
        <p className="text-sm text-red-600">
          {apiErrorMessage(error, "Could not load your resignation status.")}
        </p>
        <button
          type="button"
          onClick={refetch}
          className="mt-3 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
        >
          Retry
        </button>
      </div>
    );
  }

  const details = (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <Field label="Reason" value={record?.reason} />
      <Field
        label="Notice Period"
        value={formatNoticePeriod(record)}
      />
      <Field label="Submitted On" value={formatDate(record?.submittedDate, true)} />
      <Field label="Approved On" value={formatDate(record?.approvedDate, true)} />
      {record?.lastWorkingDay && (
        <Field label="Last Working Day" value={formatDate(record.lastWorkingDay)} />
      )}
    </div>
  );

  return (
    <div className="p-6 space-y-5">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-800">Resignation</h1>
          <p className="text-sm text-gray-500">
            Submit your resignation and complete the exit clearance.
          </p>
        </div>
        <StatusBadge status={status} />
      </header>

      {status !== RESIGNATION_STATUS.IDLE && (
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <FlowProgress status={status} />
          <ResignationActivity record={record} viewer="employee" />
        </div>
      )}

      {status === RESIGNATION_STATUS.IDLE && record?.withdrawnBy && (
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <ResignationActivity record={record} viewer="employee" />
        </div>
      )}

      {status === RESIGNATION_STATUS.IDLE && (
        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          {record?.rejectionReason && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3">
              <p className="text-sm font-medium text-red-700">
                Your previous request was rejected by HR
              </p>
              <p className="mt-1 text-sm text-red-600">{record.rejectionReason}</p>
            </div>
          )}
          <h2 className="text-lg font-semibold text-gray-800">Submit Resignation</h2>
          <form className="mt-4 space-y-4" onSubmit={handleSubmit}>
            <div>
              <label className="block text-sm font-medium text-gray-600">
                Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                rows={4}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Let your HR know why you are resigning"
                className="mt-1 w-full resize-none rounded-lg border border-gray-300 p-3 text-sm outline-none focus:border-blue-primary"
              />
            </div>
            {/* <div className="max-w-xs">
              <label className="block text-sm font-medium text-gray-600">
                Notice Period (days)
              </label>
              <input
                type="number"
                min="0"
                value={noticePeriodDays}
                onChange={(e) => setNoticePeriodDays(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 p-2 text-sm outline-none focus:border-blue-primary"
              />
            </div> */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isSubmitting}
                className="rounded-lg bg-blue-primary px-5 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50"
              >
                {isSubmitting ? "Submitting..." : "Submit Resignation"}
              </button>
            </div>
          </form>
        </section>
      )}

      {status === RESIGNATION_STATUS.PENDING && (
        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <h2 className="text-lg font-semibold text-gray-800">Awaiting HR Approval</h2>
            <button
              type="button"
              onClick={() => setShowWithdraw(true)}
              className="rounded-lg border border-red-300 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
            >
              Withdraw Resignation
            </button>
          </div>
          <div className="mt-4">{details}</div>
        </section>
      )}

      {status === RESIGNATION_STATUS.APPROVED && (
        <>
          <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-800">Resignation Approved</h2>
            <div className="mt-4">{details}</div>
          </section>
          <UploadDocuments
            employeeId={employeeId}
            rejectionReason={record?.documentsRejectionReason}
          />
        </>
      )}

      {status === RESIGNATION_STATUS.UPLOADED && (
        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-800">Documents Under Review</h2>
          <p className="mt-1 text-sm text-gray-500">
            HR is verifying your exit-clearance documents. You will be notified if anything
            needs to be re-uploaded.
          </p>
          <div className="mt-4">{details}</div>
          <div className="mt-4">
            <DocumentList documents={record?.documents} />
          </div>
        </section>
      )}

      {status === RESIGNATION_STATUS.COMPLETED && (
        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
            <p className="text-sm font-medium text-emerald-700">
              Your exit clearance is complete.
            </p>
            <p className="mt-1 text-sm text-emerald-600">
              Verified on {formatDate(record?.reviewedDocumentsDate, true)}.
            </p>
          </div>
          <div className="mt-4">{details}</div>
          <div className="mt-4">
            <DocumentList documents={record?.documents} />
          </div>
        </section>
      )}

      <ReasonDialog
        open={showWithdraw}
        title="Withdraw resignation?"
        description="Your request will be cancelled and HR will no longer see it. You can submit a new resignation later."
        confirmLabel="Withdraw"
        confirmClassName="bg-red-500 hover:bg-red-600"
        isLoading={isWithdrawing}
        onConfirm={handleWithdraw}
        onClose={() => setShowWithdraw(false)}
      />
    </div>
  );
};

export default EmployeeResignation;
